import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { supabase, waitForAuthSync } from '../../lib/supabase';
import { getRouteForRole, getPostLoginRedirect } from '../../lib/roleRoutes';
import Icon from '../../components/AppIcon';

const LoginAuthentication = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('mobile'); // 'mobile' | 'email'
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotIdentifier, setForgotIdentifier] = useState('');
  const [forgotIdentifierType, setForgotIdentifierType] = useState('mobile');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotNewPin, setForgotNewPin] = useState('');
  const [forgotConfirmPin, setForgotConfirmPin] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotMaskedEmail, setForgotMaskedEmail] = useState('');
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // ── If already logged in, redirect to dashboard ─────────────────
  useEffect(() => {
    let mounted = true;
    const checkAuth = async () => {
      try {
        const role = localStorage.getItem('userRole');
        if (role) {
          // Verify Supabase session is still valid before redirecting
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            navigate(getPostLoginRedirect() || getRouteForRole(role), { replace: true });
            return;
          }
          // Session expired — clear stale localStorage to prevent redirect loop
          ['userRole','userEmail','userName','userId','accessToken'].forEach(k => localStorage.removeItem(k));
        }
      } catch (e) {
        // Any auth-check failure must NOT blank the page — clear stale state and show login
        ['userRole','userEmail','userName','userId','accessToken'].forEach(k => localStorage.removeItem(k));
      }
      if (mounted) setIsCheckingAuth(false);
    };
    checkAuth();
    return () => { mounted = false; };
  }, []);

  // ── Route mapping by role — shared with auth-callback ─────────
  // (see src/lib/roleRoutes.js)

  // ── Google Login ──────────────────────────────────────────────
  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      const redirectBase = 'https://sugarhathi.com';
      // Carry ?next through the OAuth round-trip so auth-callback can restore intent
      const next = getPostLoginRedirect();
      const redirectTo = next
        ? `${redirectBase}/auth-callback?next=${encodeURIComponent(next)}`
        : `${redirectBase}/auth-callback`;
      const { error } = await supabase?.auth?.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo
        }
      });
      if (error) {
        setError('Google login failed. Please try again.');
        setGoogleLoading(false);
      }
    } catch (err) {
      setError('Google login failed. Please try again.');
      setGoogleLoading(false);
    }
  };

  // ── Mobile Login ──────────────────────────────────────────────
  const handleMobileLogin = async (e) => {
    e?.preventDefault();
    setError('');
    const anonKey = supabase?.supabaseKey || import.meta.env?.VITE_SUPABASE_ANON_KEY;

    if (!mobile?.trim() || !password?.trim()) {
      setError('Please enter mobile number and PIN.');
      return;
    }
    if (!/^[6-9]\d{9}$/?.test(mobile?.trim())) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }
    if (!/^\d{4}$/.test(password?.trim())) {
      setError('PIN must be exactly 4 digits.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        'https://dfliwixthxiocdfzpbvt.supabase.co/functions/v1/user-signin',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + anonKey,
            'apikey': anonKey
          },
          body: JSON.stringify({
            mobile_phone: mobile?.trim(),
            email: '',
            pin: password?.trim()
          })
        }
      );
      const result = await response?.json();

      if (!response?.ok) {
        setError(result?.error || 'Invalid mobile number or PIN.');
        setIsLoading(false);
        return;
      }

      // Set Supabase session from edge function response
      if (result?.session?.access_token) {
        await supabase?.auth?.setSession({
          access_token: result?.session?.access_token,
          refresh_token: result?.session?.refresh_token || ''
        });
        // Wait for AuthContext's listener to actually pick up the new
        // session before navigating — otherwise ProtectedRoute still sees
        // no user and bounces straight back to this page.
        await waitForAuthSync();
      }

      localStorage.setItem('userRole', result?.user?.role);
      localStorage.setItem('userEmail', result?.user?.email);
      localStorage.setItem('userName', result?.user?.name);
      localStorage.setItem('userId', result?.user?.id);
      localStorage.setItem('accessToken', result?.session?.access_token);

      navigate(getPostLoginRedirect() || getRouteForRole(result?.user?.role));

    } catch (err) {
      setError('Login failed. Please try again.');
    }

    setIsLoading(false);
  };

  // ── Email Login ───────────────────────────────────────────────
  const handleEmailLogin = async (e) => {
    e?.preventDefault();
    setError('');
    const anonKey = supabase?.supabaseKey || import.meta.env?.VITE_SUPABASE_ANON_KEY;

    if (!email?.trim()) {
      setError('Please enter your email address.');
      return;
    }
    if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/?.test(email?.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!emailPassword?.trim()) {
      setError('Please enter your PIN.');
      return;
    }
    if (!/^\d{4}$/.test(emailPassword?.trim())) {
      setError('PIN must be exactly 4 digits.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        'https://dfliwixthxiocdfzpbvt.supabase.co/functions/v1/user-signin',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + anonKey,
            'apikey': anonKey
          },
          body: JSON.stringify({
            mobile_phone: '',
            email: email?.trim()?.toLowerCase(),
            pin: emailPassword?.trim()
          })
        }
      );
      const result = await response?.json();

      if (!response?.ok) {
        setError(result?.error || 'Invalid email or PIN. Please check your credentials.');
        setIsLoading(false);
        return;
      }

      // Set Supabase session from edge function response
      if (result?.session?.access_token) {
        await supabase?.auth?.setSession({
          access_token: result?.session?.access_token,
          refresh_token: result?.session?.refresh_token || ''
        });
        // Wait for AuthContext's listener to actually pick up the new
        // session before navigating — otherwise ProtectedRoute still sees
        // no user and bounces straight back to this page.
        await waitForAuthSync();
      }

      localStorage.setItem('userRole', result?.user?.role);
      localStorage.setItem('userEmail', result?.user?.email);
      localStorage.setItem('userName', result?.user?.name);
      localStorage.setItem('userId', result?.user?.id);
      localStorage.setItem('accessToken', result?.session?.access_token);

      navigate(getPostLoginRedirect() || getRouteForRole(result?.user?.role));

    } catch (err) {
      setError('Login failed. Please try again.');
    }

    setIsLoading(false);
  };

  // ── Forgot Password ───────────────────────────────────────────
  const FORGOT_URL = 'https://dfliwixthxiocdfzpbvt.supabase.co/functions/v1/forgot-pin';
const getForgotHeaders = () => {
  const anonKey = supabase?.supabaseKey || import.meta.env?.VITE_SUPABASE_ANON_KEY;
  return { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + anonKey, 'apikey': anonKey };
};

const handleForgotSendOtp = async (e) => {
  e?.preventDefault();
  setForgotError('');
  if (!forgotIdentifier?.trim()) {
    setForgotError(forgotIdentifierType === 'mobile' ? 'Please enter your mobile number.' : 'Please enter your email address.');
    return;
  }
  if (forgotIdentifierType === 'mobile' && !/^[6-9]\d{9}$/.test(forgotIdentifier?.trim())) {
    setForgotError('Please enter a valid 10-digit mobile number.');
    return;
  }
  if (forgotIdentifierType === 'email' && !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(forgotIdentifier?.trim())) {
    setForgotError('Please enter a valid email address.');
    return;
  }
  setForgotLoading(true);
  try {
    const body = forgotIdentifierType === 'mobile'
      ? { action: 'send_otp', Phone: '+91' + forgotIdentifier?.trim(), email: '' }
      : { action: 'send_otp', Phone: '', email: forgotIdentifier?.trim()?.toLowerCase() };
    const res = await fetch(FORGOT_URL, { method: 'POST', headers: getForgotHeaders(), body: JSON.stringify(body) });
    const data = await res?.json();
    if (!res?.ok) { setForgotError(data?.error || 'Failed to send OTP. Please try again.'); }
    else { setForgotMaskedEmail(data?.masked_email || ''); setForgotStep(2); }
  } catch { setForgotError('Network error. Please try again.'); }
  setForgotLoading(false);
};

const handleForgotVerifyOtp = async (e) => {
  e?.preventDefault();
  setForgotError('');
  if (!forgotOtp?.trim() || !/^\d{6}$/.test(forgotOtp?.trim())) {
    setForgotError('Please enter the 6-digit OTP from your email.');
    return;
  }
  setForgotLoading(true);
  try {
    const body = forgotIdentifierType === 'mobile'
      ? { action: 'verify_otp', Phone: '+91' + forgotIdentifier?.trim(), email: '', otp_code: forgotOtp?.trim() }
      : { action: 'verify_otp', Phone: '', email: forgotIdentifier?.trim()?.toLowerCase(), otp_code: forgotOtp?.trim() };
    const res = await fetch(FORGOT_URL, { method: 'POST', headers: getForgotHeaders(), body: JSON.stringify(body) });
    const data = await res?.json();
    if (!res?.ok) { setForgotError(data?.error || 'Invalid or expired OTP.'); }
    else { setForgotStep(3); }
  } catch { setForgotError('Network error. Please try again.'); }
  setForgotLoading(false);
};

const handleForgotResetPin = async (e) => {
  e?.preventDefault();
  setForgotError('');
  if (!forgotNewPin?.trim() || !/^\d{4}$/.test(forgotNewPin?.trim())) {
    setForgotError('PIN must be exactly 4 digits.');
    return;
  }
  if (forgotNewPin !== forgotConfirmPin) {
    setForgotError('PINs do not match.');
    return;
  }
  setForgotLoading(true);
  try {
    const body = forgotIdentifierType === 'mobile'
      ? { action: 'reset_pin', Phone: '+91' + forgotIdentifier?.trim(), email: '', otp_code: forgotOtp?.trim(), new_pin: forgotNewPin?.trim() }
      : { action: 'reset_pin', Phone: '', email: forgotIdentifier?.trim()?.toLowerCase(), otp_code: forgotOtp?.trim(), new_pin: forgotNewPin?.trim() };
    const res = await fetch(FORGOT_URL, { method: 'POST', headers: getForgotHeaders(), body: JSON.stringify(body) });
    const data = await res?.json();
    if (!res?.ok) { setForgotError(data?.error || 'Failed to reset PIN. Please try again.'); }
    else { setForgotSuccess(true); }
  } catch { setForgotError('Network error. Please try again.'); }
  setForgotLoading(false);
};

const resetForgotModal = () => {
  setShowForgotModal(false);
  setForgotStep(1);
  setForgotIdentifier('');
  setForgotIdentifierType('mobile');
  setForgotOtp('');
  setForgotNewPin('');
  setForgotConfirmPin('');
  setForgotError('');
  setForgotSuccess(false);
  setForgotMaskedEmail('');
};

  // ── Render ────────────────────────────────────────────────────
  if (isCheckingAuth) return null;
  return (
    <>
      <Helmet>
        <title>Login - SugarHathi | Secure Authentication</title>
        <meta name="description" content="Sign in to SugarHathi - India's leading sugar commodity trading and logistics platform." />
      </Helmet>

      <div className="min-h-screen flex items-center justify-center px-4 py-10" style={{ backgroundColor: '#f8f1e9' }}>
        {/* Background blobs */}
        <div className="absolute top-16 left-8 w-40 h-40 rounded-full opacity-20 blur-3xl" style={{ backgroundColor: '#f59e0b' }} />
        <div className="absolute bottom-16 right-8 w-48 h-48 rounded-full opacity-15 blur-3xl" style={{ backgroundColor: '#1e4a72' }} />

        <div className="relative z-10 w-full max-w-md">
          {/* Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-amber-100 p-8">

            {/* Logo + Title */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl mb-4 overflow-hidden shadow-md ring-2 ring-amber-200 cursor-pointer" onClick={() => navigate('/')} title="SugarHathi home">
                <img
                  src="/assets/images/ChatGPT_Image_Dec_1_2025_08_15_51_PM-1764613791088.png"
                  alt="SugarHathi logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="text-2xl font-bold" style={{ color: '#1e4a72' }}>SugarHathi</h1>
              <p className="text-sm mt-1" style={{ color: '#6b7280' }}>Connecting Mills, Movers &amp; Markets</p>
            </div>

            {/* Google Login */}
            <button
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border-2 font-semibold text-sm transition-all hover:shadow-md active:scale-95 mb-6"
              style={{ borderColor: '#e5e7eb', color: '#374151', backgroundColor: '#fff' }}
            >
              {googleLoading ? (
                <div className="w-5 h-5 border-2 border-gray-300 border-t-amber-500 rounded-full animate-spin" />
              ) : (
                <svg width="20" height="20" viewBox="0 0 48 48" fill="none">
                  <path d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z" fill="#FFC107"/>
                  <path d="M6.3 14.7l7 5.1C15.1 16.1 19.2 13 24 13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 16.3 2 9.7 7.4 6.3 14.7z" fill="#FF3D00"/>
                  <path d="M24 46c5.5 0 10.5-1.9 14.3-5.1l-6.6-5.6C29.7 36.9 27 38 24 38c-6.1 0-10.7-3.1-11.8-7.5l-7 5.4C8.6 42.5 15.8 46 24 46z" fill="#4CAF50"/>
                  <path d="M44.5 20H24v8.5h11.8c-.6 2.3-2 4.3-3.8 5.8l6.6 5.6C42.7 36.3 45 30.6 45 24c0-1.3-.2-2.7-.5-4z" fill="#1976D2"/>
                </svg>
              )}
              {googleLoading ? 'Redirecting to Google...' : 'Continue with Google'}
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1 h-px" style={{ backgroundColor: '#e5e7eb' }} />
              <span className="text-xs font-medium" style={{ color: '#9ca3af' }}>or sign in with</span>
              <div className="flex-1 h-px" style={{ backgroundColor: '#e5e7eb' }} />
            </div>

            {/* Tabs */}
            <div className="flex rounded-xl overflow-hidden border mb-5" style={{ borderColor: '#e5e7eb' }}>
              <button
                type="button"
                onClick={() => { setActiveTab('mobile'); setError(''); }}
                className="flex-1 py-2.5 text-sm font-semibold transition-all flex items-center justify-center gap-1.5"
                style={{
                  backgroundColor: activeTab === 'mobile' ? '#1e4a72' : '#f9fafb',
                  color: activeTab === 'mobile' ? '#ffffff' : '#6b7280'
                }}
              >
                <Icon name="Smartphone" size={15} />
                Mobile
              </button>
              <button
                type="button"
                onClick={() => { setActiveTab('email'); setError(''); }}
                className="flex-1 py-2.5 text-sm font-semibold transition-all flex items-center justify-center gap-1.5"
                style={{
                  backgroundColor: activeTab === 'email' ? '#1e4a72' : '#f9fafb',
                  color: activeTab === 'email' ? '#ffffff' : '#6b7280'
                }}
              >
                <Icon name="Mail" size={15} />
                Email
              </button>
            </div>

            {/* ── Mobile Login Form ── */}
            {activeTab === 'mobile' && (
              <form onSubmit={handleMobileLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#374151' }}>
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    placeholder="10-digit mobile number"
                    value={mobile}
                    onChange={e => setMobile(e?.target?.value)}
                    maxLength={10}
                    className="w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 transition"
                    style={{ borderColor: '#d1d5db', color: '#1f2937', backgroundColor: '#fafafa' }}
                  />
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#374151' }}>
                    4-digit PIN
                  </label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your 4-digit PIN"
                    value={password}
                    onChange={e => setPassword(e?.target?.value)}
                    maxLength={4}
                    className="w-full px-4 py-2.5 pr-11 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 transition"
                    style={{ borderColor: '#d1d5db', color: '#1f2937', backgroundColor: '#fafafa' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-9 text-gray-400 hover:text-amber-600 transition-colors"
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                  </button>
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-lg text-sm" style={{ backgroundColor: '#fef2f2', color: '#dc2626' }}>
                    <Icon name="AlertCircle" size={16} />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 rounded-xl font-semibold text-sm text-white transition-all hover:opacity-90 active:scale-95 flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#1e4a72' }}
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Icon name="LogIn" size={16} />
                  )}
                  {isLoading ? 'Signing In...' : 'Login'}
                </button>

                <div className="text-center mt-2">
                  <button
                    type="button"
                    onClick={() => { resetForgotModal(); setShowForgotModal(true); }}                    
                    className="text-sm font-medium hover:underline transition-colors"
                    style={{ color: '#f59e0b' }}
                  >
                    Forgot PIN?
                  </button>
                </div>
              </form>
            )}

            {/* ── Email Login Form ── */}
            {activeTab === 'email' && (
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#374151' }}>
                    Email ID
                  </label>
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={e => setEmail(e?.target?.value)}
                    className="w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 transition"
                    style={{ borderColor: '#d1d5db', color: '#1f2937', backgroundColor: '#fafafa' }}
                  />
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#374151' }}>
                    4-digit PIN
                  </label>
                  <input
                    type={showEmailPassword ? 'text' : 'password'}
                    placeholder="Enter your 4-digit PIN"
                    value={emailPassword}
                    onChange={e => setEmailPassword(e?.target?.value)}
                    maxLength={4}
                    className="w-full px-4 py-2.5 pr-11 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 transition"
                    style={{ borderColor: '#d1d5db', color: '#1f2937', backgroundColor: '#fafafa' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowEmailPassword(!showEmailPassword)}
                    className="absolute right-3 top-9 text-gray-400 hover:text-amber-600 transition-colors"
                  >
                    <Icon name={showEmailPassword ? 'EyeOff' : 'Eye'} size={18} />
                  </button>
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-lg text-sm" style={{ backgroundColor: '#fef2f2', color: '#dc2626' }}>
                    <Icon name="AlertCircle" size={16} />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 rounded-xl font-semibold text-sm text-white transition-all hover:opacity-90 active:scale-95 flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#1e4a72' }}
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Icon name="LogIn" size={16} />
                  )}
                  {isLoading ? 'Signing In...' : 'Login'}
                </button>

                <div className="text-center mt-2">
                  <button
                    type="button"
                    onClick={() => { setShowForgotModal(true); setForgotIdentifier(email || ''); setForgotIdentifierType('email'); setForgotSuccess(false); setForgotError(''); }}
                    className="text-sm font-medium hover:underline transition-colors"
                    style={{ color: '#f59e0b' }}
                  >
                    Forgot PIN?
                  </button>
                </div>
              </form>
            )}

            {/* Register link */}
            <p className="text-center text-sm mt-6" style={{ color: '#6b7280' }}>
              New User?{' '}
              <button
                onClick={() => {
                  // Carry ?next through to registration so a brand-new user who
                  // arrived here mid-journey (e.g. from Mandi's Buy Sugar) still
                  // lands back on their intended destination after signing up.
                  const next = getPostLoginRedirect();
                  navigate(next ? `/register?next=${encodeURIComponent(next)}` : '/register');
                }}
                className="font-semibold hover:underline transition-colors"
                style={{ color: '#f59e0b' }}
              >
                Register here
              </button>
            </p>
          </div>

          <p className="text-center text-xs mt-6" style={{ color: '#9ca3af' }}>
            &copy; {new Date()?.getFullYear()} SugarHathi. All rights reserved.
          </p>
        </div>
      </div>

      {/* Forgot PIN Modal */}
      {showForgotModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold" style={{ color: '#1e4a72' }}>Reset PIN</h2>
        <button type="button" onClick={resetForgotModal}>
          <Icon name="X" size={20} />
        </button>
      </div>

      {forgotSuccess ? (
        <div className="text-center py-4">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-3" style={{ backgroundColor: '#d1fae5' }}>
            <Icon name="CheckCircle" size={28} color="#059669" />
          </div>
          <p className="font-semibold mb-1" style={{ color: '#1f2937' }}>PIN Reset Successful!</p>
          <p className="text-sm mb-4" style={{ color: '#6b7280' }}>You can now login with your new PIN.</p>
          <button onClick={resetForgotModal} className="w-full py-2.5 rounded-xl font-semibold text-sm text-white" style={{ backgroundColor: '#1e4a72' }}>
            Back to Login
          </button>
        </div>

      ) : forgotStep === 1 ? (
        <form onSubmit={handleForgotSendOtp} className="space-y-4">
          <p className="text-sm" style={{ color: '#6b7280' }}>Enter your registered mobile number or email. OTP will be sent to your registered email.</p>
          <div className="flex rounded-xl overflow-hidden border" style={{ borderColor: '#d1d5db' }}>
            <button type="button"
              onClick={() => { setForgotIdentifierType('mobile'); setForgotIdentifier(''); setForgotError(''); }}
              className="flex-1 py-2 text-sm font-semibold transition"
              style={{ backgroundColor: forgotIdentifierType === 'mobile' ? '#1e4a72' : '#fff', color: forgotIdentifierType === 'mobile' ? '#fff' : '#374151' }}>
              Mobile
            </button>
            <button type="button"
              onClick={() => { setForgotIdentifierType('email'); setForgotIdentifier(''); setForgotError(''); }}
              className="flex-1 py-2 text-sm font-semibold transition"
              style={{ backgroundColor: forgotIdentifierType === 'email' ? '#1e4a72' : '#fff', color: forgotIdentifierType === 'email' ? '#fff' : '#374151' }}>
              Email
            </button>
          </div>
          <input
            type={forgotIdentifierType === 'mobile' ? 'tel' : 'email'}
            placeholder={forgotIdentifierType === 'mobile' ? '10-digit mobile number' : 'Email address'}
            value={forgotIdentifier}
            onChange={e => setForgotIdentifier(e?.target?.value)}
            maxLength={forgotIdentifierType === 'mobile' ? 10 : 100}
            className="w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            style={{ borderColor: '#d1d5db', color: '#1f2937' }}
          />
          {forgotError && <p className="text-sm" style={{ color: '#dc2626' }}>{forgotError}</p>}
          <button type="submit" disabled={forgotLoading} className="w-full py-2.5 rounded-xl font-semibold text-sm text-white flex items-center justify-center gap-2" style={{ backgroundColor: '#1e4a72' }}>
            {forgotLoading ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : 'Send OTP'}
          </button>
        </form>

      ) : forgotStep === 2 ? (
        <form onSubmit={handleForgotVerifyOtp} className="space-y-4">
          <p className="text-sm" style={{ color: '#6b7280' }}>OTP sent to <strong>{forgotMaskedEmail}</strong>. Enter the 6-digit code below.</p>
          <input type="text" placeholder="6-digit OTP" value={forgotOtp} onChange={e => setForgotOtp(e?.target?.value)} maxLength={6}
            className="w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            style={{ borderColor: '#d1d5db', color: '#1f2937' }} />
          {forgotError && <p className="text-sm" style={{ color: '#dc2626' }}>{forgotError}</p>}
          <button type="submit" disabled={forgotLoading} className="w-full py-2.5 rounded-xl font-semibold text-sm text-white flex items-center justify-center gap-2" style={{ backgroundColor: '#1e4a72' }}>
            {forgotLoading ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : 'Verify OTP'}
          </button>
          <button type="button" onClick={() => { setForgotStep(1); setForgotError(''); }} className="w-full text-sm text-center" style={{ color: '#6b7280' }}>← Back</button>
        </form>

      ) : (
        <form onSubmit={handleForgotResetPin} className="space-y-4">
          <p className="text-sm" style={{ color: '#6b7280' }}>Enter your new 4-digit PIN.</p>
          <input type="password" placeholder="New 4-digit PIN" value={forgotNewPin} onChange={e => setForgotNewPin(e?.target?.value)} maxLength={4}
            className="w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            style={{ borderColor: '#d1d5db', color: '#1f2937' }} />
          <input type="password" placeholder="Confirm new PIN" value={forgotConfirmPin} onChange={e => setForgotConfirmPin(e?.target?.value)} maxLength={4}
            className="w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            style={{ borderColor: '#d1d5db', color: '#1f2937' }} />
          {forgotError && <p className="text-sm" style={{ color: '#dc2626' }}>{forgotError}</p>}
          <button type="submit" disabled={forgotLoading} className="w-full py-2.5 rounded-xl font-semibold text-sm text-white flex items-center justify-center gap-2" style={{ backgroundColor: '#1e4a72' }}>
            {forgotLoading ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : 'Reset PIN'}
          </button>
        </form>
      )}
    </div>
  </div>
)}
    </>
  );
};

export default LoginAuthentication;
